import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  Form, message, Radio
} from 'antd'
import {
  FilterOutlined,
  PrinterOutlined,
  TagsOutlined,
  IdcardOutlined
} from '@ant-design/icons'

// Import custom components
import Button from '../../components/common/button/button'
import Modal from '../../components/common/modal/modal'
import Table from '../../components/common/table/table'
import Spinner from '../../components/common/spinner/spinner'
import ClearanceLabelsPrintPreview from '../../components/clearanceLabels/ClearanceLabelsPrintPreview'

import {
  validateEditChange,
  getClearanceLabelsColumns,
  getEditColumns,
  fetchDataUtil,
  getColumnSearchProps,
  getDateFilterProps,
  getCustomFilterWithSearch
} from '../clearanceLabels/clearanceLabelsListUtils'
import { fetchClearanceLabel } from '../../api/clearanceLabelApiService/clearanceLabelApiUtils' 

// Import styles
import './addClearanceLabels.scss'

const ClearanceLabels = () => {
  // State management
  const [isReloading, setIsReloading] = useState(false)
  const [rows, setRows] = useState([])
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editRowData, setEditRowData] = useState(null)
  const [messageApi, contextHolder] = message.useMessage()
  const [pageSize, setPageSize] = useState(10)
  const [tableFilters, setTableFilters] = useState({})
  const [showPrintPreview, setShowPrintPreview] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [labelLayout, setLabelLayout] = useState('detailed')

  const formEditRow = Form.useForm()[0]

  // References
  const printPreviewRef = useRef(null)
  const hasFetched = useRef(false)

  // MSAL instance
  const { instance } = useMsal()

  // Fetch data function for reuse
  const fetchData = async () => {
    await fetchDataUtil(instance, setRows, setIsReloading, messageApi)
  }

  // Initial data load
  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    fetchData()
  }, [])

  // Handle edit button click
  const handleMainEdit = async (row) => {
    const keyCodes = (row?.keyCodes || []).map(String)
    try {
      setIsReloading(true)
      const response = await fetchClearanceLabel(instance, keyCodes)
      const labelData = response?.data || []

      const priceMap = {}
      labelData.forEach(item => {
        priceMap[item.keyCode] = item
      })

      const printList = keyCodes.map(keyCode => {
        const priceInfo = priceMap[keyCode] || {}
        return {
          apn: keyCode,
          productDescription: priceInfo.productDescription || '',
          wasPrice: priceInfo.originalPrice ?? priceInfo.recommendRetailPriceIncTaxInPesos ?? 0,
          currentPrice: priceInfo.recommendRetailPriceIncTaxInPesos ?? 0,
          quantity: 1
        }
      })

      setEditRowData({ ...row, guid: row._id, printList })
      setEditModalVisible(true)
    } catch (error) {
      const errorMsg = error?.response?.data?.error || 'Failed to fetch label data'
      messageApi.error(errorMsg)
    } finally {
      setIsReloading(false)
    }
  }

  // Handle print from modal — expands each item by its quantity (fully frontend)
  const handlePrintFromEdit = () => {
    if (!editRowData?.printList) return

    const expandedPrintList = editRowData.printList.flatMap(item =>
      Array.from({ length: item.quantity || 1 }, () => ({ ...item }))
    )

    setPrintData({ ...editRowData, printList: expandedPrintList })
    setShowPrintPreview(true)

    setTimeout(() => {
      const styleElement = document.createElement('style')
      styleElement.id = 'print-fixes-style'
      styleElement.textContent = `
        @media print {
          @page { size: A4; margin: 0; }
          html, body { height: auto !important; overflow: visible !important; }
        }
      `
      document.head.appendChild(styleElement)
      window.print()
      setTimeout(() => {
        const el = document.getElementById('print-fixes-style')
        if (el) document.head.removeChild(el)
        setShowPrintPreview(false)
      }, 500)
    }, 300)
  }

  // Use a ref so handleEditTableChange always reads the latest editRowData
  // without needing to be recreated on every render
  const editRowDataRef = useRef(editRowData)
  editRowDataRef.current = editRowData

  // Handle editing table data
  const handleEditTableChange = useCallback((value, record, field) => {
    if (!validateEditChange(field, value, messageApi)) return

    let updatedValue = value
    if (field === 'quantity') {
      updatedValue = parseInt(value, 10)
    } else if (field === 'wasPrice' || field === 'currentPrice') {
      updatedValue = parseFloat(value)
    }

    const current = editRowDataRef.current
    const updatedPrintList = current.printList.map((item) =>
      item.apn === record.apn ? { ...item, [field]: updatedValue } : item
    )

    setEditRowData({ ...current, printList: updatedPrintList })
  }, [messageApi])

  // Handle pagination change
  const handlePageSizeChange = (current, size) => {
    setPageSize(size)
  }

  // Handle table changes (including filters)
  const handleTableChange = (pagination, filters) => {
    setTableFilters(filters)
  }

  // Clear all filters
  const handleClearAllFilters = () => {
    setTableFilters({})
  }

  // Get unique created by values for filter dropdown
  const createdByOptions = [...new Set(rows.map(item => item.createdBy))]
    .filter(Boolean)

  // Table columns with proper filters
  const baseColumns = getClearanceLabelsColumns(handleMainEdit)
  const editColumns = getEditColumns(handleEditTableChange)

  const enhancedColumns = [
    {
      ...baseColumns[0], // Label Name column
      dataIndex: 'name',
      filteredValue: tableFilters.name || null,
      ...getColumnSearchProps('name', 'Label Name')
    },
    {
      ...baseColumns[1], // Created By column
      dataIndex: 'createdBy',
      filteredValue: tableFilters.createdBy || null,
      ...getCustomFilterWithSearch('createdBy', createdByOptions)
    },
    {
      ...baseColumns[2], // Created Date column
      dataIndex: 'createdAt',
      filteredValue: tableFilters.createdAt || null,
      ...getDateFilterProps()
    },
    baseColumns[3] // Actions column (no filter needed)
  ]

  // Sort data by created date (newest first)
  const sortedData = [...rows].sort((a, b) =>
    new Date(b.createdAt) - new Date(a.createdAt)
  )

  const hasFiltersApplied = Object.keys(tableFilters).some(key =>
    tableFilters[key]?.length > 0
  )

  return (
    <>
      {contextHolder}

      <div className="clearance-labels">
        <h4 className="clearance-labels-title">Clearance Reports</h4>
        <hr />

        {/* Filter summary and clear button */}
        {hasFiltersApplied && (
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              onClick={handleClearAllFilters}
              icon={<FilterOutlined />}
              type="default"
              size="small"
            >
              Clear All Filters
            </Button>
          </div>
        )}

        <div className="clearance-labels-container">
          {/* Wrap table area with Spinner for localized loading */}
          <div style={{ position: 'relative', minHeight: '400px' }}>
            {isReloading && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Spinner style={{ transform: 'scale(1.5)' }} />
              </div>
            )}
            <Table
              dataSource={sortedData}
              columns={enhancedColumns}
              rowKey="_id"
              onChange={handleTableChange}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                onChange: (page, pageSize) => {
                  setPageSize(pageSize)
                },
                onShowSizeChange: handlePageSizeChange,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
              }}
              className="clearance-labels-table"
              scroll={{ x: 'max-content', y: 550 }}
              style={{ opacity: isReloading ? 0.5 : 1 }}
            />
          </div>
        </div>

        {/* Edit Modal */}
        <Modal
          title="Print Clearance Report"
          open={editModalVisible}
          onCancel={() => setEditModalVisible(false)}
          width={800}
          footer={[
            <Button key="cancel" onClick={() => setEditModalVisible(false)}>
              Cancel
            </Button>,
            <Button
              key="print"
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrintFromEdit}
            >
              Print
            </Button>
          ]}
          className="clearance-labels-edit-modal"
        >
          {editRowData && (
            <Form form={formEditRow} layout="vertical">
              <div style={{ marginBottom: 16, padding: '10px 0' }}>
                <span style={{ fontSize: 17, color: '#333', marginRight: 16, fontWeight: 600 }}>Layout: </span>
                <Radio.Group className="layout-radio-group" value={labelLayout} onChange={e => setLabelLayout(e.target.value)}>
                  <Radio value="detailed" style={{ fontSize: 16 }}><TagsOutlined style={{ marginRight: 6 }} />Label</Radio>
                  <Radio value="classic" style={{ fontSize: 16 }}><IdcardOutlined style={{ marginRight: 6 }} />Shelftalker</Radio>
                </Radio.Group>
              </div>
              <Table
                dataSource={editRowData.printList}
                columns={editColumns}
                rowKey="apn"
                pagination={false}
                className="clearance-labels-edit-table"
                scroll={{ x: 'max-content', y: 350 }}
              />
            </Form>
          )}
        </Modal>

      </div>

      {/* Print Preview Content - A4 Label Format - Only shown during print */}
      {showPrintPreview && printData && (
        <div ref={printPreviewRef}>
          <ClearanceLabelsPrintPreview printList={printData.printList} layout={labelLayout} />
        </div>
      )}
    </>
  )
}

export default ClearanceLabels
